// src/features/affiliate/admin/ConversionsTab.tsx
// Admin: review tracked conversions, approve/reject, or record one manually
// for a sale that happened outside standard tracking (phone order, CSV
// reconciliation, etc.) — TrackConversion is built for exactly this.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useAffiliateConversion, useAffiliateTracking, useI18n } from '@react/hooks';
import { Button, Input, Alert } from '@react/components';
import { FormDialog, FormRow } from '@/components/form';
import { Select } from '@/components/Select';
import { useScope } from '@/hooks/useScope';
import type { Conversion, ConversionStatus } from '@core/types/affiliate';
import { ConversionStatusBadge } from '../badges';
import { errMessage, formatMoney, listParams, makeEntity } from '../format';

const STATUS_FILTERS: (ConversionStatus | '')[] = ['', 'CONVERSION_STATUS_PENDING', 'CONVERSION_STATUS_APPROVED', 'CONVERSION_STATUS_REJECTED', 'CONVERSION_STATUS_HOLD'];

export const ConversionsTab = observer(() => {
  const { t, locale } = useI18n();
  const scope = useScope();
  const store = useAffiliateConversion();
  const trackingStore = useAffiliateTracking();

  const [status, setStatus] = useState<ConversionStatus | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<Conversion | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [manualOpen, setManualOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedDetail, setExpandedDetail] = useState<Conversion | null>(null);
  const [expandLoading, setExpandLoading] = useState(false);

  // manual conversion form
  const [clickId, setClickId] = useState('');
  const [goal, setGoal] = useState('');
  const [revenue, setRevenue] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [sourceType, setSourceType] = useState('order');
  const [manualSaving, setManualSaving] = useState(false);

  const load = () => {
    setError(null);
    store.ListConversions(listParams({ limit: 100 }, { status: status || undefined })).catch((e: unknown) => setError(errMessage(e, t('affiliate.admin.conversions.loadError'))));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const approve = async (c: Conversion) => {
    if (!c.entity) return;
    setBusyId(c.entity.id);
    setError(null);
    try {
      await store.ApproveConversion(c.entity.id, { entity: c.entity });
      load();
    } catch (e) {
      setError(errMessage(e, t('affiliate.admin.conversions.actionError')));
    } finally {
      setBusyId(null);
    }
  };

  const toggleExpand = async (c: Conversion) => {
    const id = c.entity?.id ?? '';
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedId(id);
    setExpandedDetail(null);
    setExpandLoading(true);
    try {
      setExpandedDetail(id ? await store.GetConversion(id) : c);
    } catch (e) {
      setError(errMessage(e, t('affiliate.admin.conversions.loadError')));
    } finally {
      setExpandLoading(false);
    }
  };

  const reject = async () => {
    if (!rejectTarget?.entity) return;
    setBusyId(rejectTarget.entity.id);
    setError(null);
    try {
      await store.RejectConversion(rejectTarget.entity.id, { entity: rejectTarget.entity, reason: rejectReason });
      setRejectTarget(null);
      setRejectReason('');
      load();
    } catch (e) {
      setError(errMessage(e, t('affiliate.admin.conversions.actionError')));
    } finally {
      setBusyId(null);
    }
  };

  const recordManual = async () => {
    if (!clickId.trim() || !goal.trim()) return;
    setManualSaving(true);
    setError(null);
    try {
      const amountMinor = revenue ? Math.round(Number(revenue) * 100) : 0;
      await trackingStore.TrackConversion({
        entity: makeEntity('', 'conversion', scope),
        click_id: clickId.trim(),
        goal: goal.trim(),
        revenue: { amount: amountMinor, currency: 'CURRENCY_USD', decimal_places: 2 },
        transaction_id: transactionId,
        sub_id_1: '',
        sub_id_2: '',
        sub_id_3: '',
        source_type: sourceType,
        idempotency_key: `manual-${Date.now()}`,
        metadata: {},
      });
      setManualOpen(false);
      setClickId('');
      setGoal('');
      setRevenue('');
      setTransactionId('');
      load();
    } catch (e) {
      setError(errMessage(e, t('affiliate.admin.conversions.manualSaveError')));
    } finally {
      setManualSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as ConversionStatus | '')}
          className="w-48"
          options={STATUS_FILTERS.map((s) => ({ value: s, label: s ? t(`affiliate.conversionStatus.${s.replace('CONVERSION_STATUS_', '').toLowerCase()}`) : t('common.all') }))}
        />
        <Button size="sm" variant="secondary" onClick={() => setManualOpen(true)}>{t('affiliate.admin.conversions.recordManual')}</Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}
      {store.isLoading && store.items.length === 0 && <div className="h-24 animate-pulse rounded-md bg-muted" aria-busy="true" />}
      {!store.isLoading && store.items.length === 0 && !error && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('affiliate.conversions.empty')}</p>
        </div>
      )}

      <div className="space-y-2">
        {store.items.map((c) => (
          <div key={c.entity?.id} className="rounded-md border border-border p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <button type="button" onClick={() => toggleExpand(c)} className="min-w-0 flex-1 text-left">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">{c.goal}</span>
                  <ConversionStatusBadge status={c.status} />
                </div>
                <p className="text-sm text-muted-foreground">
                  {formatMoney(c.revenue, locale)} · {t('affiliate.admin.conversions.payout')}: {formatMoney(c.payout, locale)}
                  {c.country && ` · ${c.country}`}
                </p>
              </button>
              {c.status === 'CONVERSION_STATUS_PENDING' && (
                <span className="flex shrink-0 gap-1.5">
                  <Button size="sm" onClick={() => approve(c)} isLoading={busyId === c.entity?.id}>{t('affiliate.admin.conversions.approve')}</Button>
                  <Button size="sm" variant="danger" onClick={() => setRejectTarget(c)}>{t('affiliate.admin.conversions.reject')}</Button>
                </span>
              )}
            </div>
            {expandedId === c.entity?.id && (
              <div className="mt-2 border-t border-border pt-2 text-xs text-muted-foreground">
                {expandLoading && <p>{t('common.loading')}</p>}
                {!expandLoading && expandedDetail && (
                  <dl className="grid grid-cols-2 gap-1">
                    <div><dt className="inline">{t('affiliate.admin.conversions.transactionId')}: </dt><dd className="inline text-foreground">{expandedDetail.transaction_id || '—'}</dd></div>
                    <div><dt className="inline">{t('affiliate.admin.conversions.sourceType')}: </dt><dd className="inline text-foreground">{expandedDetail.source_type || '—'}</dd></div>
                    <div><dt className="inline">IP: </dt><dd className="inline text-foreground">{expandedDetail.ip || '—'}</dd></div>
                    <div><dt className="inline">{t('affiliate.admin.conversions.payout')} %: </dt><dd className="inline text-foreground">{expandedDetail.payout_percent || '—'}</dd></div>
                    {expandedDetail.reject_reason && (
                      <div className="col-span-2"><dt className="inline">{t('affiliate.admin.conversions.rejectTitle')}: </dt><dd className="inline text-foreground">{expandedDetail.reject_reason}</dd></div>
                    )}
                  </dl>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {rejectTarget && (
        <div className="rounded-md border border-border p-3">
          <p className="text-sm font-medium text-foreground">{t('affiliate.admin.conversions.rejectTitle')}</p>
          <Input className="mt-2" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={t('affiliate.admin.conversions.rejectReasonPlaceholder')} />
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="danger" onClick={reject} isLoading={busyId === rejectTarget.entity?.id}>{t('affiliate.admin.conversions.reject')}</Button>
            <Button size="sm" variant="ghost" onClick={() => setRejectTarget(null)}>{t('common.cancel')}</Button>
          </div>
        </div>
      )}

      <FormDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onSubmit={recordManual}
        submitLabel={t('common.create')}
        canSubmit={!!clickId.trim() && !!goal.trim()}
        isSubmitting={manualSaving}
        dirty={!!(clickId.trim() || goal.trim())}
        width="md"
        aria-label={t('affiliate.admin.conversions.recordManual')}
      >
        <div className="space-y-1 px-3.5 pb-3 pt-3">
          <p className="pb-1 text-sm text-muted-foreground">{t('affiliate.admin.conversions.manualHint')}</p>
          <FormRow label={t('affiliate.admin.conversions.clickId')} labelWidth="sm">
            <input
              value={clickId}
              onChange={(e) => setClickId(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label={t('affiliate.admin.conversions.goal')} labelWidth="sm">
            <input
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="purchase"
              className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label={t('affiliate.admin.offers.revenue')} labelWidth="sm">
            <input
              type="text"
              inputMode="decimal"
              value={revenue}
              onChange={(e) => setRevenue(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="0.00"
              className="w-28 rounded-md border border-input bg-background px-2.5 py-1.5 text-right text-sm tabular-nums text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label={t('affiliate.admin.conversions.transactionId')} labelWidth="sm">
            <input
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label={t('affiliate.admin.conversions.sourceType')} labelWidth="sm">
            <input
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value)}
              placeholder="order"
              className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
        </div>
      </FormDialog>
    </div>
  );
});
